import {Divider, Link as MuiLink, Typography} from '@mui/material';
import {Box} from '@jobseeker/lotus/foundations';
import {Link} from '@jobseeker/lotus/elements';
import Menu from '../../Menu/Menu';
import {StyledTab, StyledSpan, StyledTabs} from '../style';
import {Image} from '@jobseeker/lotus/components/Image';
import {handleTracking} from '@jobseeker/common-core/utils/analytics';
import {openUrl} from '@jobseeker/common-core/utils/common';
import {TrackEventUBA} from '@jobseeker/common-core/utils';

import {shallowEqual, useSelector} from 'react-redux';
import {useEffect, useState} from 'react';
import {getFunctionalAreaClusterById} from '@jobseeker/common-core/utils/categories';
import type {BaseSyntheticEvent, SyntheticEvent} from 'react';
import type {NavPropType} from '../types';
import type {AppState} from '@jobseeker/common-core/types';
import type {MenuItemType} from '@jobseeker/lotus/components/Menu/types';

const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

const NavContent = (props: NavPropType) => {
  const {
    tabsList = [],
    courses = [],
    theme,
    value,
    open,
    anchorEl,
    selected,
    moreMenu,
    selectedNav,
    toShowSelect,
    showCourses,
    courseLink,
    showPremium,
    handleOnClick,
    handleTabChange,
    handleMenuOpen,
    handleMenuClose,
  } = props || {};

  const othersList = [
    {
      heading: 'Others',
      label: 'Others',
      dataId: '26',
      tag: 'others',
      menu:
        Array.isArray(tabsList) && tabsList.length > 6
          ? tabsList.slice(3, tabsList.length - 3)
          : [],
    },
  ];

  const specializedList = [
    {
      heading: 'Specialized Jobs',
      label: 'Specialized Jobs',
      dataId: '28',
      tag: 'specialized jobs',
      menu: (() => {
        const len = Array.isArray(tabsList) ? tabsList.length : 0;
        if (len === 0) return [];
        const start = 6;
        const end = len - 4;
        const range: number[] = [];
        for (let i = start; i <= end; i++) {
          if (i >= 0 && i < len) range.push(i);
        }
        try {
          const order = [len - 3, len - 2, len - 1, ...range];
          return order
            .map(i => (i >= 0 && i < len ? tabsList[i] : undefined))
            .filter(Boolean);
        } catch (err) {
          console.warn(err);
          return [] as any[];
        }
      })(),
    },
  ];

  const isUserLogin: boolean = useSelector(
    (state: AppState) => state.auth.isLoggedIn,
    shallowEqual
  );

  const {profile} = useSelector((state: AppState) => state.user);

  const [faCluster, setFaCluster] = useState<string | number | null>(null);

  useEffect(() => {
    const id = (profile as any)?.functionalArea?.id ?? null;
    if (id == null) return;
    let active = true;
    (async () => {
      try {
        const cluster = await getFunctionalAreaClusterById(id);
        if (!active) return;
        setFaCluster(cluster ?? null);
      } catch (e) {
        console.log('Functional Area Cluster: lookup failed');
      }
    })();
    return () => {
      active = false;
    };
  }, [profile?.functionalArea?.id]);

  const [isAuthLoading, setIsAuthLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsAuthLoading(false);
    }, 700);

    return () => clearTimeout(timer);
  }, []);

  let menu: string | any[] = [];

  if (value > -1) {
    if (value === 12) {
      menu = courses;
    } else {
      menu =
        moreMenu > 3
          ? tabsList.slice(3) || [{}]
          : isAppIimjobs
            ? value < tabsList.length
              ? tabsList[value].menu
              : isUserLogin &&
                  (faCluster === 'healthcare' || faCluster === 'pharma')
                ? othersList[0]?.menu
                : specializedList[0]?.menu
            : tabsList[value].menu;
    }
  }

  const isMenuSelected = (index: number) => {
    if (toShowSelect) {
      return false;
    }

    if (selectedNav > -1) {
      return selectedNav === index;
    }
    return selected === index;
  };

  const handlePremiumClick = () => {
    const event = 'jsClickGoPremium';

    if (window && window.location) {
      const urlParams = new URLSearchParams(window.location.search);
      const ref = urlParams.get('ref') ?? '';

      handleTracking(event, {
        event_category: 'Subscription',
        Origin: 'Jobfeed',
        RefText: ref,
        UserId: sessionStorage.getItem('userId') || 'NA',
        Device: sessionStorage.getItem('device') || 'desktop',
        Device_Type: sessionStorage.getItem('deviceType') || 'web',
        Status: sessionStorage.getItem('status') || 'LoggedOut',
      });

      openUrl(`/subscribe/plans`, '_self');
    }
  };

  const getRelativeTagLink = (menu: MenuItemType[]) => {
    return menu && menu?.length > 0 && menu[0]?.tag
      ? `/c/${menu[0].tag
          .replace('&', '')
          .replace(/\s+/g, '-')
          .toLowerCase()}-jobs${!isAppIimjobs ? '?ref=topnavigation' : ''}`
      : '#';
  };

  const categoryId = useSelector(
    (state: AppState) => state.app.context.navigation.actualCategory
  );
  const tagId = useSelector(
    (state: AppState) => state.app.context.navigation.tag,
    shallowEqual
  );
  const page = useSelector((state: AppState) => state.app.page, shallowEqual);

  const trackEvent = (clickedMenuName: string) => {
    TrackEventUBA('topMenuClick', 'click', {
      categoryID: page === 'category' ? categoryId : undefined,
      tagID: page === 'tag' ? Number(tagId) : undefined,
      page,
      sessionUsername: sessionStorage.getItem('userId') || '',
      clickedMenuName,
    });
  };
  const orderListInd = (() => {
    try {
      return [
        tabsList.length - 3,
        tabsList.length - 2,
        tabsList.length - 1,
        0,
        1,
        2,
      ];
    } catch (err) {
      console.warn(err);
      return [0, 1, 2, 3, 4, 5];
    }
  })();
  return (
    <Box
      display={{xxs: 'none', lg: 'flex'}}
      position={'relative'}
      marginLeft={theme.spacing(6.25)}
      onMouseLeave={handleMenuClose}
    >
      {tabsList?.map((tab, i) => {
        const url = tab?.tag
          ? `/c/${tab?.tag
              .replace('&', '')
              .replace(/\s+/g, '-')
              .toLowerCase()}-jobs?ref=${
              isAppIimjobs ? 'nav' : 'topnavigation'
            }`
          : !isAppIimjobs
            ? getRelativeTagLink(menu)
            : '#';
        return (
          <Box key={i} sx={{display: 'none'}}>
            <Box key={`nav-content-box-${i}`}>
              <a
                href={url}
                target="_self"
                style={{
                  textDecoration: 'none',
                  color: 'inherit',
                }}
              >
                {tab?.label}
              </a>
              {tab.menu?.map((submenu, j) => {
                const href = `/k/${submenu.tag
                  ?.replace(/&/g, '')
                  .replace(/\s+/g, '-')
                  .toLowerCase()}-jobs?ref=${
                  isAppIimjobs ? 'nav' : 'topnavigation'
                }`;

                return (
                  <Box key={j}>
                    <Typography>
                      <a
                        href={href}
                        onClick={e => {
                          e.preventDefault();
                          setTimeout(() => {
                            trackEvent(submenu.label);
                            openUrl(href, '_self');
                          }, 0);
                        }}
                        style={{textDecoration: 'none', color: 'inherit'}}
                        target="_self"
                      >
                        {submenu.label}
                      </a>
                    </Typography>

                    {Array.isArray(submenu.menu) && (
                      <Box component="ul" sx={{pl: 2, m: 0}}>
                        {submenu.menu.map((menu, k) => {
                          const nestedHref = `/k/${menu.tag
                            ?.replace(/&/g, '')
                            .replace(/\s+/g, '-')
                            .toLowerCase()}-jobs?ref=${
                            isAppIimjobs ? 'nav' : 'topnavigation'
                          }`;
                          return (
                            <Box
                              component="li"
                              key={k}
                              sx={{listStyle: 'disc'}}
                            >
                              <Typography component="span">
                                <a
                                  href={nestedHref}
                                  onClick={e => {
                                    e.preventDefault();
                                    setTimeout(() => {
                                      trackEvent(menu.label);
                                      openUrl(nestedHref, '_self');
                                    }, 0);
                                  }}
                                  style={{
                                    textDecoration: 'none',
                                    color: 'inherit',
                                  }}
                                  target="_self"
                                >
                                  {menu.label}
                                </a>
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        );
      })}

      <StyledTabs
        value={value > -1 ? (value === 12 ? 0 : value) : 0}
        onChange={handleTabChange}
        TabIndicatorProps={{sx: {display: 'none'}}}
      >
        {isAppIimjobs ? (
          <>
            {isAuthLoading ? (
              <></>
            ) : isUserLogin &&
              (faCluster === 'healthcare' || faCluster === 'pharma') ? (
              <>
                {orderListInd
                  .map(idx => tabsList[idx])
                  .filter(Boolean)
                  .map((tabs, index) => {
                    const {label, tag, menu} = tabs || {};
                    const dataId = parseInt(tabs?.dataId, 10);
                    const url = tag
                      ? `/c/${tag
                          .replace('&', '')
                          .replace(/\s+/g, '-')
                          .toLowerCase()}-jobs?ref=${
                          isAppIimjobs ? 'nav' : 'topnavigation'
                        }`
                      : !isAppIimjobs
                        ? getRelativeTagLink(menu)
                        : '#';
                    return (
                      <>
                        <Box
                          key={`nav-content-box-${orderListInd[index]}`}
                          display="block"
                        >
                          <a
                            href={url}
                            onClick={e => {
                              e.preventDefault();
                              setTimeout(() => {
                                if (url !== '#') {
                                  trackEvent(label);
                                  openUrl(url, '_self');
                                }
                              }, 0);
                            }}
                            target="_self"
                            style={{textDecoration: 'none', color: 'inherit'}}
                          >
                            <StyledTab
                              value={orderListInd[index]}
                              theme={theme}
                              onMouseEnter={e =>
                                handleMenuOpen(e, orderListInd[index])
                              }
                              data-key={orderListInd[index]}
                              label={label}
                              onClick={e => {
                                setTimeout(() => {
                                  handleOnClick(e, orderListInd[index]);
                                  handleMenuClose();
                                }, 0);
                              }}
                              aria-owns={open ? 'menu-list-grow' : undefined}
                              aria-haspopup="true"
                              sx={{
                                color: isMenuSelected(dataId)
                                  ? theme.palette.primary.main
                                  : '',
                              }}
                            />
                            <StyledSpan
                              theme={theme}
                              visible={isMenuSelected(dataId)}
                            />
                          </a>
                        </Box>
                        {orderListInd[index] == tabsList.length - 1 && (
                          <Divider
                            orientation="vertical"
                            variant="middle"
                            sx={{
                              border: isAppIimjobs
                                ? '0.60px #333333 solid'
                                : '0.60px solid #e9e8e8',
                              my: `${theme.spacing(2.75)} !important`,
                            }}
                            flexItem
                          />
                        )}
                      </>
                    );
                  })}

                {othersList?.map((otherTabs, oIdx) => {
                  const {label, tag, menu} = otherTabs || {};
                  const dataId = parseInt(otherTabs?.dataId, 10);
                  const offset = tabsList.length;
                  const tabValue = offset + oIdx;
                  const url =
                    tag &&
                    Array.isArray(menu) &&
                    menu.length > 0 &&
                    menu[0]?.tag
                      ? `/c/${String(menu[0]?.tag)
                          .replace('&', '')
                          .replace(/\s+/g, '-')
                          .toLowerCase()}-jobs?ref=${
                          isAppIimjobs ? 'nav' : 'topnavigation'
                        }`
                      : !isAppIimjobs
                        ? getRelativeTagLink(menu)
                        : '#';
                  return (
                    <Box
                      key={`nav-content-box-others-${tabValue}`}
                      display="block"
                    >
                      <a
                        href={url}
                        onClick={e => {
                          e.preventDefault();
                          setTimeout(() => {
                            if (url !== '#') {
                              trackEvent(label);
                              openUrl(url, '_self');
                            }
                          }, 0);
                        }}
                        target="_self"
                        style={{textDecoration: 'none', color: 'inherit'}}
                      >
                        <StyledTab
                          value={tabValue}
                          theme={theme}
                          onMouseEnter={e => handleMenuOpen(e, tabValue)}
                          data-key={tabValue}
                          label={label}
                          onClick={e => {
                            setTimeout(() => {
                              handleOnClick(e, tabValue);
                              handleMenuClose();
                            }, 0);
                          }}
                          aria-owns={open ? 'menu-list-grow' : undefined}
                          aria-haspopup="true"
                          sx={{
                            color: isMenuSelected(dataId)
                              ? theme.palette.primary.main
                              : '',
                          }}
                        />
                        <StyledSpan
                          theme={theme}
                          visible={isMenuSelected(dataId)}
                        />
                      </a>
                    </Box>
                  );
                })}
              </>
            ) : (
              <>
                {tabsList.slice(0, tabsList.length - 5).map((tabs, index) => {
                  const {label, tag, menu} = tabs || {};
                  const dataId = parseInt(tabs?.dataId);
                  const url = tag
                    ? `/c/${tag
                        .replace('&', '')
                        .replace(/\s+/g, '-')
                        .toLowerCase()}-jobs?ref=${
                        isAppIimjobs ? 'nav' : 'topnavigation'
                      }`
                    : !isAppIimjobs
                      ? getRelativeTagLink(menu)
                      : '#';
                  return (
                    <Box key={`nav-content-box-${index}`} display="block">
                      <a
                        href={url}
                        onClick={e => {
                          e.preventDefault();
                          setTimeout(() => {
                            if (url !== '#') {
                              trackEvent(label);
                              openUrl(url, '_self');
                            }
                          }, 0);
                        }}
                        target="_self"
                        style={{
                          textDecoration: 'none',
                          color: 'inherit',
                        }}
                      >
                        <StyledTab
                          value={index}
                          theme={theme}
                          onMouseEnter={(
                            event: BaseSyntheticEvent<object, any, any>
                          ) => {
                            handleMenuOpen(event, index);
                          }}
                          data-key={index}
                          label={label}
                          onClick={(event: SyntheticEvent<Element, Event>) => {
                            setTimeout(() => {
                              handleOnClick(event, index);
                              handleMenuClose();
                            }, 0);
                          }}
                          aria-owns={open ? 'menu-list-grow' : undefined}
                          aria-haspopup={'true'}
                          sx={{
                            color: isMenuSelected(dataId)
                              ? theme.palette.primary.main
                              : '',
                          }}
                        />
                        {
                          <StyledSpan
                            theme={theme}
                            visible={isMenuSelected(dataId)}
                          />
                        }
                      </a>
                    </Box>
                  );
                })}
                <Divider
                  orientation="vertical"
                  variant="middle"
                  sx={{
                    border: isAppIimjobs
                      ? '0.60px #333333 solid'
                      : '0.60px solid #e9e8e8',
                    my: `${theme.spacing(2.75)} !important`,
                  }}
                  flexItem
                />
                {specializedList?.map((otherTabs, oIdx) => {
                  const {label, tag, menu} = otherTabs || {};
                  const parentDataId = parseInt(otherTabs?.dataId, 10);
                  const childDataIds: number[] = Array.isArray(menu)
                    ? (menu as any[])
                        .map(m => parseInt(m?.dataId as string, 10))
                        .filter(n => !Number.isNaN(n))
                    : [];
                  const effectiveDataId =
                    typeof selectedNav === 'number' &&
                    childDataIds.includes(selectedNav)
                      ? selectedNav
                      : parentDataId;
                  const dataId = effectiveDataId;
                  const offset = tabsList.length;
                  const tabValue = offset + oIdx;
                  const url =
                    tag &&
                    Array.isArray(menu) &&
                    menu.length > 0 &&
                    menu[0]?.tag
                      ? `/c/${String(menu[0]?.tag)
                          .replace('&', '')
                          .replace(/\s+/g, '-')
                          .toLowerCase()}-jobs?ref=${
                          isAppIimjobs ? 'nav' : 'topnavigation'
                        }`
                      : !isAppIimjobs
                        ? getRelativeTagLink(menu)
                        : '#';
                  return (
                    <>
                      <Box
                        key={`nav-content-box-others-${tabValue}`}
                        display="block"
                      >
                        {oIdx === 0 && (
                          <Box
                            sx={{
                              top: '3px',
                              right: 8,
                              width: '38px',
                              height: '17px',
                              borderRadius: '40px',
                              background: '#E5EFFE',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              opacity: 1,
                              zIndex: 2,
                              marginTop: '6px',
                            }}
                          >
                            <Typography
                              sx={{
                                width: '23px',
                                height: '16px',
                                fontWeight: 500,
                                fontStyle: 'italic',
                                fontSize: '9px',
                                lineHeight: '16px',
                                letterSpacing: '1px',
                                textAlign: 'center',
                                color: '#4C6FFF',
                                opacity: 1,
                              }}
                            >
                              NEW
                            </Typography>
                          </Box>
                        )}
                        <a
                          href={url}
                          onClick={e => {
                            e.preventDefault();
                            setTimeout(() => {
                              if (url !== '#') {
                                trackEvent(label);
                                openUrl(url, '_self');
                              }
                            }, 0);
                          }}
                          target="_self"
                          style={{textDecoration: 'none', color: 'inherit'}}
                        >
                          <StyledTab
                            value={tabValue}
                            theme={theme}
                            onMouseEnter={e => handleMenuOpen(e, tabValue)}
                            data-key={tabValue}
                            label={label}
                            onClick={e => {
                              setTimeout(() => {
                                handleOnClick(e, tabValue);
                                handleMenuClose();
                              }, 0);
                            }}
                            aria-owns={open ? 'menu-list-grow' : undefined}
                            aria-haspopup="true"
                            sx={{
                              color: isMenuSelected(dataId)
                                ? theme.palette.primary.main
                                : '',
                              ...(label === 'Specialized Jobs' && {
                                justifyContent: 'flex-start',
                                minHeight: '30px',
                                pb: '19px',
                              }),
                            }}
                          />
                          <StyledSpan
                            theme={theme}
                            visible={isMenuSelected(dataId)}
                          />
                        </a>
                      </Box>
                    </>
                  );
                })}
              </>
            )}
          </>
        ) : (
          <>
            {tabsList.map((tabs, index) => {
              const {label, tag, menu} = tabs || {};
              const dataId = parseInt(tabs?.dataId);
              const url = tag
                ? `/c/${tag
                    .replace('&', '')
                    .replace(/\s+/g, '-')
                    .toLowerCase()}-jobs?ref=${
                    isAppIimjobs ? 'nav' : 'topnavigation'
                  }`
                : !isAppIimjobs
                  ? getRelativeTagLink(menu)
                  : '#';
              return (
                <Box key={`nav-content-box-${index}`} display="block">
                  <a
                    href={url}
                    onClick={e => {
                      e.preventDefault();
                      setTimeout(() => {
                        if (url !== '#') {
                          trackEvent(label);
                          openUrl(url, '_self');
                        }
                      }, 0);
                    }}
                    target="_self"
                    style={{
                      textDecoration: 'none',
                      color: 'inherit',
                    }}
                  >
                    <StyledTab
                      value={index}
                      theme={theme}
                      onMouseEnter={(
                        event: BaseSyntheticEvent<object, any, any>
                      ) => {
                        handleMenuOpen(event, index);
                      }}
                      data-key={index}
                      label={label}
                      onClick={(event: SyntheticEvent<Element, Event>) => {
                        setTimeout(() => {
                          handleOnClick(event, index);
                          handleMenuClose();
                        }, 0);
                      }}
                      aria-owns={open ? 'menu-list-grow' : undefined}
                      aria-haspopup={'true'}
                      sx={{
                        color: isMenuSelected(dataId)
                          ? theme.palette.primary.main
                          : '',
                      }}
                    />
                    {
                      <StyledSpan
                        theme={theme}
                        visible={isMenuSelected(dataId)}
                      />
                    }
                  </a>
                </Box>
              );
            })}
          </>
        )}
      </StyledTabs>

      {isAuthLoading ? (
        <></>
      ) : (
        showCourses && (
          <Box
            display={{xxs: 'none', md: 'inline-flex'}}
            marginLeft={theme.spacing(1.5)}
          >
            <Divider
              orientation="vertical"
              variant="middle"
              sx={{
                border: isAppIimjobs
                  ? '0.60px #333333 solid'
                  : '0.60px solid #e9e8e8',
                my: `${theme.spacing(2.75)} !important`,
              }}
              flexItem
            />

            <Link
              sx={{
                display: {
                  xxs: 'none',
                  sm: 'inline-flex',
                },
                marginLeft: theme.spacing(1.5),
                marginTop: theme.spacing(2.5),
                opacity: 1,
                padding: 0,
                fontWeight: isAppIimjobs
                  ? theme.typography.fontWeightMedium
                  : theme.typography.fontWeightLight,
                ':hover': {
                  textDecoration: 'none',
                },
              }}
              href={courseLink}
              target="_blank"
              onClick={(e: any) => {
                e.preventDefault();
                if (courseLink) {
                  setTimeout(() => openUrl(courseLink, '_blank'), 0);
                }
              }}
              onMouseEnter={(event: React.SyntheticEvent) => {
                handleTabChange(event, 12);
                handleMenuOpen(event, 12);
              }}
            >
              Courses{''}
            </Link>
          </Box>
        )
      )}

      {isAuthLoading ? (
        <></>
      ) : (
        !!showPremium && (
          <MuiLink
            onClick={() => {
              setTimeout(() => {
                handlePremiumClick();
              }, 0);
            }}
            display={'flex'}
            height={'fit-content'}
            sx={{
              m: '11px 0 0 25px',
              p: '11px 13px 12px',
              borderRadius: 2,
              backgroundColor: '#13355c',
              textDecoration: 'none',
              fontSize: 12,
              fontWeight: 700,
              color: '#feae02!important',
              textTransform: 'uppercase',
              cursor: 'pointer',
              ':hover': {
                backgroundColor: '#1c4370',
              },
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                fontSize: 10,
                fontWeight: 700,
                color: '#fff',
                right: '6px',
                top: '4px',
                width: '32px',
                padding: '2px 3px 0 4px',
                borderRadius: '3px',
                backgroundColor: '#feae02',
              }}
            >
              NEW
            </Box>
            <Image
              src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADQAAAAiCAMAAAAnKQpfAAAABGdBTUEAALGPC/xhBQAAAAFzUkdCAK7OHOkAAABIUExURUdwTP//4f+/Kf/FLv+/KP/CLf+/KP+/Kf/AKf/KNv/AKf+/KP+/KP/AKv+/Kf+/Kf+/KP+/KP/BK/+/Kf+/KP+/Kf+/Kf++KAX0DW0AAAAXdFJOUwABqQ+/Gfv0PARS6uBEebRp8CnXyJSBRhWSlQAAAZRJREFUOMvFVduygyAMVJSLoIKg3f//0xOo9UCFzvSpOx2nQBI3mwS77tfgzBjGv3KZ+AbC9p2XBePcwrYt+kHrMma/Yei6AVtfcs7snATEIz+fhJhez/9IDwFI91xowDIBl8cccUzTgTHfcxDMAjot9mi/YM4NvIGUMD7fm7FEzz0tZOTfmfS8+Omons59Bpj03NIqQFGOokz6wEG/UhxBKiiEtFoxOx2wLXlYCe8h85cvG4J2M9YnFRupkIKZgCyWiIqVSZdMSLOXoPrYwVaBkV8WkjTw8orDR4iVYT/yNLkQXBkYFwmp8dRAUxwVyTo6UtGm7ApGXDmjIoed2s6c+S2GGnAPVFTLKXf21kpUJ2KibOQ9r5cAwzrThrAUpE91KrstPLd67snjUoT+DIp2+lfYEq+X91OjrdkpdtHptzTfprJ6XolUY1KTojlzdxlyKepohaQeHJuYG+Q5PqIuUy+gmgAaCZdz2N0nsIYA33Ly5+zdsccRrkOdV0PthtRTA7p5b7JP4rGGkzayCaN/8h36A5tNHYcT17qLAAAAAElFTkSuQmCC"
              alt="crown_img"
              width={26}
              height={17}
              style={{
                marginRight: '6px',
              }}
            />
            Go Premium
          </MuiLink>
        )
      )}

      {menu?.length > 0 && (
        <Menu
          indexTab={value}
          value={moreMenu}
          open={open}
          anchorEl={anchorEl}
          menuItems={menu}
          handleMenuClose={handleMenuClose}
          theme={theme}
          width={isAppIimjobs ? '280px' : '100%'}
        />
      )}
    </Box>
  );
};

export default NavContent;
